import React from 'react';
import { OrderDestination } from '@hero/schema';
import { useTranslation } from 'react-i18next';
import { LocationIcon, MapPinnedIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';
import { SavedLocationDisplay } from '../../SavedLocation/Name/SavedLocationDisplay';
import { ClientDisplay } from '../../Client/ClientDisplay';
import { ContactsTooltipDisplay } from '../../Contact/ContactsTooltipDisplay';
import { RegionName } from '../../../app/RegionName/RegionName';

export interface OrderDestinationDisplayV2Props {
  destination: OrderDestination;
  showSavedPlace?: boolean;
  showAddress?: boolean;
  showCustomer?: boolean;
  showContact?: boolean;
  /** Hide the icon column (used when parent controls the icon/timeline layout) */
  hideIcon?: boolean;
  className?: string;
}

/**
 * OrderDestinationDisplayV2 - Displays a destination with circular icon badge and detailed information
 *
 * Features:
 * - Circular icon badge (blue for pickup, green for dropoff)
 * - City name as header
 * - Optional: Saved place name (using SavedLocationDisplay badge), full address, customer name, contact info
 * - RTL support via Tailwind rtl: utilities
 * - hideIcon mode for timeline layouts where parent controls the icon column
 *
 * @example
 * ```tsx
 * <OrderDestinationDisplayV2
 *   destination={pickup}
 *   showSavedPlace
 *   showAddress
 *   showCustomer
 * />
 * ```
 */
export const OrderDestinationDisplayV2: React.FC<OrderDestinationDisplayV2Props> = ({
  destination,
  showSavedPlace = true,
  showAddress = true,
  showCustomer = true,
  showContact = false,
  hideIcon = false,
  className = '',
}) => {
  const { t } = useTranslation('m-order');

  const isPickup = destination.type === 'pickup';
  const iconBgColor = isPickup ? 'bg-blue-100' : 'bg-green-100';
  const iconColor = isPickup ? 'text-blue-600' : 'text-green-600';
  const Icon = isPickup ? LocationIcon : MapPinnedIcon;

  const defaultTitle = isPickup ? t('destination.pickup') : t('destination.dropoff');
  const cityName = destination.address?.city?.name || defaultTitle;
  const cityCustomCode = destination.address?.city?.customCode;

  const content = (
    <div className="flex-1 space-y-1.5 pt-0.5">
      {/* City + Place badge ... Customer badge (spread apart) */}
      <div className={cn('flex items-center gap-2 flex-wrap')}>
        <h4 className="text-[14px] font-[600] leading-[22px] text-gray-900">
          {cityCustomCode ? (
            <RegionName customCode={cityCustomCode} fallback={cityName} />
          ) : (
            cityName
          )}
        </h4>
        {showSavedPlace && destination.savedPlace?.name && (
          <SavedLocationDisplay
            savedLocationName={destination.savedPlace.name}
            className="text-[11px] px-1.5 py-0.5"
          />
        )}
        {showCustomer && destination.beneficiary?.name && (
          <>
            <div className="flex-1" />
            <ClientDisplay
              clientName={destination.beneficiary.name}
              className="text-[11px] px-1.5 py-0.5"
            />
          </>
        )}
      </div>

      {/* Full Address */}
      {showAddress && destination.address?.fullAddress && (
        <p
          className={cn(
            'text-[13px] leading-[19px] text-gray-600 text-start',
          )}>
          {destination.address.fullAddress}
        </p>
      )}

      {/* Contact Info */}
      {showContact && destination.contacts && destination.contacts.length > 0 && (
        <div className="text-start">
          <ContactsTooltipDisplay
            contacts={destination.contacts.map(c => ({
              name: c.name,
              phone: c.phone,
              email: c.email,
            }))}
            headerTitle={t('destination.contact')}
          />
        </div>
      )}
    </div>
  );

  // When hideIcon is true, only render the content (parent handles the icon column)
  if (hideIcon) {
    return <div className={cn('relative', className)}>{content}</div>;
  }

  return (
    <div className={cn('relative', className)}>
      <div className={cn('flex gap-3')}>
        {/* Circular Icon Badge */}
        <div className="flex flex-col items-center flex-shrink-0">
          <div
            className={cn(
              'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0',
              iconBgColor,
            )}>
            <Icon className={cn('size-5', iconColor)} />
          </div>
        </div>

        {content}
      </div>
    </div>
  );
};
